import { DashboardsAPI } from '@/API/Dashboards'
import { CredentialsAPI } from '@/API/Credentials'
import type { AppDispatch, RootState } from '@/redux'
import { chatSessionActions } from '@/redux/slice/chatSessionSlice'
import { dashboardPanelsActions } from '@/redux/slice/dashboardPanelsSlice'
import { dashboardCreationActions } from '@/redux/slice/dashboardCreationSlice'
import { parseAnthropicMessages, rawToCredentialOption } from './utils/parseAnthropicMessages'
import type { ChatMessage, TemplateChatMessage } from '@/types/Dashboards.types'
import type { CredentialOption } from '@/types/DashboardV1.types'
import { AppName } from '@/constants/application/application'
import { getDashboards } from './dashboardsService'

// ─── Template message merge ───────────────────────────────────────────────────
//
// Merges JSONB-parsed ChatMessage[] with TemplateChatMessage[] by timestamp.
//
// Tie-break rule: when two messages share the exact same millisecond timestamp,
// the template message is placed BEFORE the LLM message. Rationale: Slice 1.3
// stamps user/assistant messages at +1ms apart, and template rows are eagerly
// appended at the gate-transition moment — meaning a template narration always
// corresponds to the start of a gate, not its resolution. Placing template rows
// first at the same ts keeps the chronological read order correct (narration →
// AI response).
function mergeMessagesByTimestamp(
  jsonbMessages: ChatMessage[],
  templateMessages: TemplateChatMessage[]
): ChatMessage[] {
  const converted: ChatMessage[] = templateMessages.map(tm => ({
    role: tm.role,
    type: 'text' as const,
    kind: 'template' as const,
    content: tm.content,
    timestamp: tm.timestamp,
  }))

  return [...jsonbMessages, ...converted].sort((a, b) => {
    const diff = new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    if (diff !== 0) return diff
    // Tie-break: template before LLM at the same ts
    const aIsTemplate = a.kind === 'template'
    const bIsTemplate = b.kind === 'template'
    if (aIsTemplate && !bIsTemplate) return -1
    if (!aIsTemplate && bIsTemplate) return 1
    return 0
  })
}

// ─── appCode → DB-side appName mapping ────────────────────────────────────────
//
// The credentials list endpoint returns `appName` in DB snake_case form.
// This map mirrors HARVEST_APP_CODE_TO_APP_NAME from tcs-api so client-side
// filtering is consistent with the backend's credential ownership model.
// v1: CampMinder is the only deterministically-picked source app.
const APP_CODE_TO_APP_NAME: Record<string, string> = {
  campminder: 'camp_minder',
}

/**
 * prefetchAvailableCredentialsForApp — deterministic path for the
 * awaiting_credential gate (TDD §3d.1.E, CEO 2026-04-28).
 *
 * Called by SourcePickerBlock when the user clicks a source card so the
 * CredentialPickerGateway renders the existing-credential picker immediately,
 * WITHOUT requiring an AI round-trip.
 *
 * Fetches all credentials, filters by appName, then dispatches
 * setAvailableCredentials. An empty result is a valid signal — the gateway
 * falls through to CredentialPickerHarvestBlock.
 *
 * Errors are NOT silently swallowed (Bug #1 2026-05-14 regression fix):
 * previously, any fetch failure dispatched `setAvailableCredentials([])` with
 * no error trace, masquerading as "no credentials exist" and dead-ending users
 * who actually had credentials. Failures now log to console AND populate
 * `credentialPrefetchError` so the harvest block can surface a retry affordance.
 */
const CREDENTIAL_PREFETCH_PAGE_SIZE = 500 // widened from 100 (Bug #1 hardening 2026-05-14); TODO follow-up: server-side appCode filter + cursor pagination if any org legitimately exceeds 500 credentials.

export const prefetchAvailableCredentialsForApp = (token: string, appCode: string) =>
  async (dispatch: AppDispatch): Promise<void> => {
    const appName = APP_CODE_TO_APP_NAME[appCode]
    if (!appName) {
      // Unknown appCode — no credentials to prefetch, leave availableCredentials as [].
      dispatch(dashboardCreationActions.setAvailableCredentials([]))
      dispatch(dashboardCreationActions.setCredentialPrefetchError(null))
      return
    }

    try {
      // Fetch all credentials with the live availability probe opt-in.
      // withAvailabilityProbe=true is required here so the picker receives
      // availabilitySummary.blockedReason for the click-guard (CTO-3 CEO-locked
      // freshness-at-commitment).  The admin Credentials list must NOT pass this
      // flag — it uses sticky connectionStatus from the default (no-probe) path.
      // (Slice 1.2 probe-isolation invariant, TDD §"Consumer enumeration".)
      const resp = await CredentialsAPI.getAll(token, CREDENTIAL_PREFETCH_PAGE_SIZE, 0, true)
      if (resp.err || !resp.data) {
        const errMessage = resp.err
          ? `Credential prefetch failed: ${typeof resp.err === 'string' ? resp.err : 'API error'}`
          : 'Credential prefetch returned no data'
        console.error('[prefetchAvailableCredentialsForApp]', errMessage, { appCode, appName })
        dispatch(dashboardCreationActions.setAvailableCredentials([]))
        dispatch(dashboardCreationActions.setCredentialPrefetchError(errMessage))
        return
      }

      // The list endpoint only returns ACTIVE credentials by default (backend
      // filters by CredentialStatus.ACTIVE). Filter client-side by appName only —
      // loginMethodCode is not returned in the list response (v1 constraint).
      // All active CampMinder credentials are API-key type (harvest flow) so
      // no secondary loginMethodCode filter is needed for v1.
      const filtered = resp.data.credentials.filter(
        c => c.appName === appName
      )

      // The list endpoint returns { id, nickname, appName, status, appLogo, apiKeyExpiresAt }.
      // CEO 2026-04-28 added `apiKeyExpiresAt` so the deterministic prefetch path can
      // render the "expires in N days" badge that the AI-driven `present_credential`
      // path already renders. `campName` is intentionally omitted (CEO directive —
      // nickname is sufficient). `loginMethodCode` is also omitted; for v1 every
      // active CampMinder credential is apiKey, so no client-side filter is needed.
      // Convert via rawToCredentialOption so expiresInDays is computed by the same
      // helper used for AI-path credentials (single source of truth for the conversion).
      const credentialOptions: CredentialOption[] = filtered.map(c =>
        rawToCredentialOption({
          id: c.id,
          label: c.nickname,
          expiresAt: c.apiKeyExpiresAt ?? null,
          // Phase E Slice 2.4 — pass availabilitySummary through to CredentialOption
          // so the click-guard in CredentialPickerExistingBlock can read blockedReason.
          // availabilitySummary is absent on pre-Phase-E credentials (fail-open: advance).
          ...(c.availabilitySummary !== undefined ? { availabilitySummary: c.availabilitySummary } : {}),
        })
      )

      dispatch(dashboardCreationActions.setAvailableCredentials(credentialOptions))
      dispatch(dashboardCreationActions.setCredentialPrefetchError(null))
    } catch (err) {
      // Unexpected throw — getAll catches network errors internally, so reaching
      // here means a programming error or an unexpected exception. Log only the
      // sanitized message + safe metadata (NOT the raw error object — round-1
      // review 2026-05-21 Copilot CO1: raw Axios errors can carry request
      // config/headers including Authorization, which must not reach logs).
      const errMessage = err instanceof Error ? err.message : 'Unexpected credential prefetch error'
      console.error('[prefetchAvailableCredentialsForApp] threw', errMessage, { appCode, appName })
      dispatch(dashboardCreationActions.setAvailableCredentials([]))
      dispatch(dashboardCreationActions.setCredentialPrefetchError(errMessage))
    }
  }

/**
 * Loads the chat session from the backend on page init.
 * Parses Anthropic-format messages into ChatMessage[] for Redux.
 * When restoring an `awaiting_confirmation` session, rehydrates proposedPanels
 * so the "Build My Dashboard" button remains enabled.
 */
export const loadChatSession = (token: string, dashboardId: string) =>
  async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
    dispatch(chatSessionActions.setLoading(true))
    try {
      const resp = await DashboardsAPI.getChatSession(token, dashboardId)

      // Discard stale response only if we are PROVABLY on a different dashboard —
      // i.e. selectedDashboard is set and its id differs from the requested one.
      // When selectedDashboard is null (temporarily cleared during a token refresh
      // that re-triggers getDashboard), we allow the update through rather than
      // silently discarding it. The false-positive discard was causing the fetching
      // spinner to hang permanently on long CampMinder fetches (3.5+ min) where
      // the access token expires mid-fetch and briefly nulls selectedDashboard.
      const currentId = getState().dashboards.selectedDashboard?.id
      if (currentId !== undefined && currentId !== dashboardId) return

      if (resp.err || !resp.data) {
        dispatch(chatSessionActions.setError(resp.err ?? 'Failed to load chat session.'))
        return
      }

      dispatch(chatSessionActions.setError(null))
      const session = resp.data

      // During an active fetch, the Redux messages array contains real-time accumulated
      // messages that may not yet match the DB snapshot. If we do a full setSession while
      // still in fetching_data, we wipe those and the chat history loses the creation
      // flow messages. Only update creationStatus — don't replace messages.
      // BUT: on a cold restore (page refresh / token-refresh that cleared state), there are
      // no in-memory messages to protect, so we must do a full setSession so the sessionId
      // and any persisted messages are restored and polling can resume correctly.
      if (session.creationStatus === 'fetching_data') {
        const currentSession = getState().chatSession
        const hasHydratedSession =
          (currentSession.sessionId?.trim().length ?? 0) > 0 ||
          currentSession.messages.length > 0
        if (hasHydratedSession) {
          dispatch(chatSessionActions.setCreationStatus(session.creationStatus))
          return
        }
        // Fall through to full setSession restore for cold start
      }

      // For all other states: full session restore (needed to get panel proposals, etc.)
      const { messages: jsonbMessages, extractedCredentials } = parseAnthropicMessages(session.messages)

      // Merge template messages (from ai_chat_messages table) with JSONB messages by timestamp.
      // Template messages are deterministic gate narrations — they have a `kind: 'template'` field
      // and are sorted BEFORE LLM messages at the same timestamp (see mergeMessagesByTimestamp).
      const mergedMessages = session.templateMessages?.length
        ? mergeMessagesByTimestamp(jsonbMessages, session.templateMessages)
        : jsonbMessages

      dispatch(chatSessionActions.setSession({
        sessionId: session.sessionId,
        creationStatus: session.creationStatus,
        messages: mergedMessages,
      }))

      // ── Flow-state + flow-context hydration (bugbash/2026-04-28) ─────────────
      // These fields are only present on the extended GetChatSessionResponse shape.
      // When absent (legacy sessions or pre-deploy), skip all dispatches gracefully.
      if (session.flowState !== undefined) {
        dispatch(dashboardCreationActions.setFlowState(session.flowState))
      }
      const ctx = session.flowContext
      if (ctx !== undefined) {
        dispatch(dashboardCreationActions.setSourceAppCode(ctx.sourceAppCode ?? null))
        dispatch(dashboardCreationActions.setCredentialId(ctx.credentialId ?? null))
        dispatch(dashboardCreationActions.setSelectedDomainIds(ctx.selectedDomainIds ?? []))
        for (const [key, value] of Object.entries(ctx.domainFilterValues ?? {})) {
          dispatch(dashboardCreationActions.setDomainFilterValue({ key, value }))
        }
        if (ctx.proposedPanels !== undefined && ctx.proposedPanels.length > 0) {
          dispatch(dashboardCreationActions.setProposedPanels(ctx.proposedPanels))
        }

        // Re-prefetch availableCredentials on hard-refresh: this list is org-
        // level (not session-state) and is normally populated by the
        // deterministic prefetch fired on source-pick. After a refresh the
        // user is back on Step 2 (awaiting_credential) but availableCredentials
        // is empty — the harvest-mode harvest block then renders instead of
        // the existing-credentials picker, blocking users who already have
        // credentials. Fire the prefetch to re-populate from the live list.
        // Bug discovered 2026-04-29 during bugbash refresh-persistence test.
        if (ctx.sourceAppCode) {
          void dispatch(prefetchAvailableCredentialsForApp(token, ctx.sourceAppCode))
        }
      }

      // History-parse path: populate availableCredentials from any present_credential
      // tool-calls found in stored Anthropic JSONB. Dedup is handled by the parser.
      // rawToCredentialOption computes expiresInDays from Date.now() at restore time.
      // CEO v1 constraint: all present_credential calls are treated as apiKey (no
      // loginMethodCode in payload). See parseAnthropicMessages.ts for the full comment.
      //
      // Only dispatch when non-empty — an empty list must NOT clobber the deterministic
      // prefetch. Fresh dashboards have no present_credential history, so this path always
      // returns []. Without this guard the bootstrap (triggered ~1s after the user picks a
      // source card) races with prefetchAvailableCredentialsForApp and blanks the picker.
      // CEO 2026-04-28 manual test: credential picker blinked then disappeared on new dashboards.
      const nowMs = Date.now()
      const credentialOptions: CredentialOption[] = extractedCredentials.map(raw =>
        rawToCredentialOption(raw, nowMs)
      )
      if (credentialOptions.length > 0) {
        dispatch(dashboardCreationActions.setAvailableCredentials(credentialOptions))
      }

      // Rehydrate proposedPanels when restoring an awaiting_confirmation session
      // so the "Build My Dashboard" button is correctly enabled on page return.
      //
      // Thin-preview warning lifecycle on restore (§3d.5):
      //  - The callout is only meaningful during awaiting_confirmation. Any other
      //    creationStatus clears any in-memory warning (otherwise a stale warning
      //    from a previous session survives the restore).
      //  - Within awaiting_confirmation, we set the warning only if the last proposal
      //    carried one, otherwise we clear. Matches sendChatMessage's if/else on the
      //    same flag so restored sessions converge to the same state as a live reply.
      if (session.creationStatus === 'awaiting_confirmation') {
        const lastProposal = [...mergedMessages]
          .reverse()
          .find(m => m.type === 'panel_proposals')
        if (lastProposal?.panels) {
          dispatch(chatSessionActions.setProposedPanels(lastProposal.panels))
          // Auto-select all panels on restore so "Build My Dashboard" re-enables
          dispatch(chatSessionActions.setSelectedPanelIndices(
            lastProposal.panels.map((_, i) => i)
          ))

          if (lastProposal.thinPreviewWarning != null) {
            dispatch(dashboardCreationActions.setThinPreviewWarning(lastProposal.thinPreviewWarning))
          } else {
            // Last proposal did NOT carry a warning — clear any stale in-memory one.
            dispatch(dashboardCreationActions.clearThinPreviewWarning())
          }
        } else {
          // awaiting_confirmation but no panel_proposals message — nothing to warn about.
          dispatch(dashboardCreationActions.clearThinPreviewWarning())
        }
      } else {
        // Any non-awaiting_confirmation status means no panel preview is on screen,
        // so the callout would be misleading if left visible from a previous session.
        dispatch(dashboardCreationActions.clearThinPreviewWarning())
      }
    } catch {
      dispatch(chatSessionActions.setError('Failed to load chat session.'))
    } finally {
      dispatch(chatSessionActions.setLoading(false))
    }
  }

/**
 * Sends a user message and appends both the user message and AI response
 * to the Redux slice. If the response contains panel proposals, also
 * dispatches setProposedPanels. Updates creationStatus from the response.
 */
export const sendChatMessage = (token: string, dashboardId: string, messageText: string) =>
  async (dispatch: AppDispatch, getState: () => RootState): Promise<boolean | undefined> => {
    const userMsg: ChatMessage = {
      role: 'user',
      type: 'text',
      content: messageText,
      timestamp: new Date().toISOString(),
    }
    dispatch(chatSessionActions.appendMessage(userMsg))
    dispatch(chatSessionActions.setIsSending(true))
    try {
      const resp = await DashboardsAPI.sendChatMessage(token, dashboardId, messageText)

      // Discard stale response only if we are PROVABLY on a different dashboard.
      // Same relaxed check as loadChatSession — null selectedDashboard (temporarily
      // cleared during token refresh) must not discard the response, otherwise
      // creationStatus is never set to 'fetching_data' and polling never starts.
      const currentId = getState().dashboards.selectedDashboard?.id
      if (currentId !== undefined && currentId !== dashboardId) return

      if (resp.err || !resp.data) {
        dispatch(chatSessionActions.setError(resp.err ?? 'Failed to send message.'))
        return
      }

      dispatch(chatSessionActions.setError(null))
      const result = resp.data
      const assistantMsg: ChatMessage = {
        role: 'assistant',
        type: result.message.type,
        content: result.message.content,
        choiceCards: result.message.choiceCards,
        choiceCardVariant: result.message.choiceCardVariant,
        panels: result.message.panels,
        credentialId: result.message.credentialId,
        appName: result.message.appName,
        credentialName: result.message.credentialName,
        logoUrl: result.message.logoUrl,
        credentialType: result.message.credentialType,
        expiresAt: result.message.expiresAt,
        status: result.message.status,
        filters: result.message.filters,
        thinPreviewWarning: result.message.thinPreviewWarning,
        libraryGap: result.message.libraryGap,
        timestamp: result.message.timestamp,
      }
      dispatch(chatSessionActions.appendMessage(assistantMsg))

      if (result.message.type === 'panel_proposals' && result.message.panels) {
        dispatch(chatSessionActions.setProposedPanels(result.message.panels))

        // Dispatch thin-preview warning if the backend flagged limited data (§3d.5)
        if (result.message.thinPreviewWarning != null) {
          dispatch(dashboardCreationActions.setThinPreviewWarning(result.message.thinPreviewWarning))
        } else {
          dispatch(dashboardCreationActions.clearThinPreviewWarning())
        }
      }

      // Real-time stream path: when the AI emits a present_credential tool-call,
      // the backend surfaces it as a credential_card message. Append to (or update
      // in) the existing availableCredentials list, deduped by credentialId.
      //
      // CEO v1 constraint: all credential_card messages are treated as apiKey —
      // the payload does not carry loginMethodCode in v1. If future auth types are
      // added, this branch must be revisited to filter by loginMethodCode.
      // Non-apiKey credentials are NOT silently swallowed; see parser comment.
      if (result.message.type === 'credential_card' && result.message.credentialId) {
        const credentialId = result.message.credentialId
        const existing = getState().dashboardCreation.availableCredentials
        // Use rawToCredentialOption as the single source of truth for the
        // expiresAt → expiresInDays conversion (PR #331 Claude review).
        const newOption: CredentialOption = rawToCredentialOption({
          id: credentialId,
          label: result.message.credentialName ?? result.message.appName ?? 'Credential',
          expiresAt: result.message.expiresAt ?? null,
        })
        // Replace if id already present (update), otherwise append (accumulate).
        const updatedList = existing.some(c => c.id === credentialId)
          ? existing.map(c => (c.id === credentialId ? newOption : c))
          : [...existing, newOption]
        dispatch(dashboardCreationActions.setAvailableCredentials(updatedList))
      }

      dispatch(chatSessionActions.setCreationStatus(result.creationStatus))

      return true
    } catch {
      dispatch(chatSessionActions.setError('Failed to send message.'))
    } finally {
      dispatch(chatSessionActions.setIsSending(false))
    }
  }

/**
 * createManualCredentialThunk — deterministic manual-paste path (CEO 2026-04-28).
 *
 * Called by ManualCredentialPasteModal after the user fills in the 5 CampMinder
 * credential fields. POSTs to /v1/credentials, then refreshes the available-
 * credential list via prefetchAvailableCredentialsForApp so the picker re-renders
 * with the new credential. User stays on /dashboards/new throughout.
 *
 * appId is resolved from the applications Redux slice (name === 'camp_minder').
 * nickname is auto-generated — no user input.
 */
export interface ManualCredentialParams {
  apiKey: string
  subscriptionKeyPrimary: string
  subscriptionKeySecondary?: string
  clientId?: string
  expireDate: string // ISO string
  nickname: string
}

export const createManualCredentialThunk = (token: string, params: ManualCredentialParams) =>
  async (dispatch: AppDispatch, getState: () => RootState): Promise<{ ok: boolean; error?: string }> => {
    const { applications } = getState().application
    const campMinderApp = applications.find(app => app.name === AppName.CAMPMINDER)

    if (!campMinderApp) {
      return { ok: false, error: 'CampMinder application not found. Please refresh and try again.' }
    }

    const credentialValues: Array<{ credentialAttributeCode: string; attributeValue: string }> = [
      { credentialAttributeCode: 'apiKey', attributeValue: params.apiKey },
      { credentialAttributeCode: 'subscriptionKey', attributeValue: params.subscriptionKeyPrimary },
    ]
    if (params.subscriptionKeySecondary) {
      credentialValues.push({
        credentialAttributeCode: 'subscriptionKeySecondary',
        attributeValue: params.subscriptionKeySecondary,
      })
    }
    if (params.clientId) {
      credentialValues.push({ credentialAttributeCode: 'clientId', attributeValue: params.clientId })
    }
    credentialValues.push({ credentialAttributeCode: 'expireDate', attributeValue: params.expireDate })

    const body = {
      appId: campMinderApp.id,
      loginMethodCode: 'apiKey',
      nickname: params.nickname,
      credentialValues,
    }

    try {
      const resp = await CredentialsAPI.create(token, body)
      if (resp.err || !resp.data) {
        return { ok: false, error: resp.err ?? 'Failed to save credential.' }
      }
      // Refresh the available-credential list so the picker shows the new credential
      await (dispatch as (action: unknown) => Promise<void>)(
        prefetchAvailableCredentialsForApp(token, 'campminder')
      )
      return { ok: true }
    } catch (err) {
      return { ok: false, error: err instanceof Error ? err.message : 'Unexpected error.' }
    }
  }

/**
 * Confirms the user-selected panels. Calls POST /panels with the selected
 * ProposedPanel objects. On success, sets panels in dashboardPanelsSlice
 * and marks creationStatus as 'complete'.
 */
export const confirmPanels = (token: string, dashboardId: string) =>
  async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
    const { proposedPanels, selectedPanelIndices } = getState().chatSession
    const selectedPanels = selectedPanelIndices.map(i => proposedPanels[i])

    dispatch(dashboardPanelsActions.setIsBuilding(true))
    try {
      const resp = await DashboardsAPI.confirmPanels(token, dashboardId, selectedPanels)

      // Discard stale response if the active dashboard changed while in-flight
      const currentId = getState().dashboards.selectedDashboard?.id
      if (currentId !== dashboardId) return

      if (resp.err || !resp.data) {
        dispatch(dashboardPanelsActions.setError(resp.err ?? 'Failed to confirm panels.'))
        return
      }

      dispatch(dashboardPanelsActions.setError(null))
      dispatch(dashboardPanelsActions.setPanels(resp.data.panels))
      dispatch(chatSessionActions.setCreationStatus('complete'))
      await dispatch(getDashboards(token))
    } catch {
      dispatch(dashboardPanelsActions.setError('Failed to confirm panels.'))
    } finally {
      dispatch(dashboardPanelsActions.setIsBuilding(false))
    }
  }
